<template>
  <div class="treatments-page">
    <Top />

    <template v-if="isDetail">
      <section class="page-hero" aria-label="Treatment detail header">
        <div class="container page-hero-inner">
          <h1 class="page-hero-title">{{ detailTitle }}</h1>
        </div>
      </section>

      <section class="detail-wrap" aria-label="Treatment detail">
        <div class="container">
          <div v-if="detailLoading" class="state-msg">Loading…</div>
          <div v-else-if="detailError" class="state-msg state-err">{{ detailError }}</div>
          <article v-else-if="detail" class="detail-article">
            <div class="detail-body" v-html="detailContentHtml" />
          </article>
        </div>
      </section>
    </template>

    <template v-else>
      <section class="page-hero" aria-label="Treatments header">
        <div class="container page-hero-inner">
          <h1 class="page-hero-title">INDIVIDUAL TREATMENT</h1>
        </div>
      </section>

      <section class="treatments" aria-label="Treatments list">
        <div class="container treatments-inner">
          <div v-if="listLoading" class="state-msg">Loading…</div>
          <div v-else-if="listError" class="state-msg state-err">{{ listError }}</div>
          <div v-else-if="!treatments.length" class="state-msg">No published treatments yet.</div>
          <div v-else class="grid">
            <article v-for="t in treatments" :key="t.id" class="card">
              <div class="thumb">
                <img class="thumb-img" :src="cardImage(t)" :alt="t.title || ''" loading="lazy" />
              </div>
              <div class="meta">
                <div class="label">{{ t.title }}</div>
                <button class="btn" type="button" @click="openDetail(t.id)">LEARN MORE</button>
              </div>
            </article>
          </div>
        </div>
      </section>
    </template>

    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-left">
          <h2 class="cta-title">AESTHETICS PRACTITIONER?</h2>
          <p class="cta-sub">
            Grow your business with DOSE and access new patients, forms, diary, reminders<br />
            and no show/late cancellation protection.
          </p>
        </div>
        <div class="cta-right">
          <button class="cta-btn" type="button" @click="goAbout">LEARN MORE</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Footer from '../components/footer.vue'
import Top from '../components/top.vue'
import { fetchPublicTreatmentDetail, fetchPublicTreatments } from '../api/treatment'
import { resolveAssetUrl, resolveHtmlAssetUrls } from '../utils/asset'

export default {
  name: 'TreatmentsPage',
  components: {
    Footer,
    Top,
  },
  data() {
    return {
      listLoading: true,
      listError: '',
      treatments: [],
      detailLoading: false,
      detailError: '',
      detail: null,
      defaultThumb: require('../assets/images/xiangmu01.jpg'),
    }
  },
  computed: {
    isDetail() {
      return this.$route.name === 'treatmentDetail'
    },
    detailId() {
      const id = this.$route.params && this.$route.params.id
      return id != null && id !== '' ? String(id) : ''
    },
    detailTitle() {
      if (this.detailLoading) return 'Treatment'
      if (this.detail && this.detail.title) return this.detail.title
      if (this.detailError) return 'Treatment'
      return 'Treatment'
    },
    detailContentHtml() {
      if (!this.detail || !this.detail.content) return ''
      return resolveHtmlAssetUrls(this.detail.content)
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        if (this.isDetail) {
          this.loadDetail()
        } else {
          this.detail = null
          this.detailError = ''
        }
      },
    },
  },
  mounted() {
    this.loadList()
  },
  methods: {
    cardImage(t) {
      const url = t.coverImageUrl || t.cover_image_url
      const resolved = resolveAssetUrl(url)
      return resolved || this.defaultThumb
    },
    loadList() {
      this.listLoading = true
      this.listError = ''
      fetchPublicTreatments()
        .then((list) => {
          this.treatments = list || []
        })
        .catch((e) => {
          this.listError = (e && e.message) || 'Failed to load treatments'
          this.treatments = []
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    loadDetail() {
      const id = this.detailId
      if (!id) {
        this.detailError = 'Invalid treatment'
        this.detail = null
        return
      }
      this.detailLoading = true
      this.detailError = ''
      this.detail = null
      fetchPublicTreatmentDetail(id)
        .then((row) => {
          this.detail = row
        })
        .catch((e) => {
          this.detailError = (e && e.message) || 'Failed to load treatment'
          this.detail = null
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    openDetail(id) {
      this.$router.push({ name: 'treatmentDetail', params: { id: String(id) } }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
    goAbout() {
      this.$router.push({ name: 'about' }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
  },
}
</script>

<style scoped>
.treatments-page {
  min-height: 100vh;
  background: #ffffff;
  color: #0c0b06;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-hero {
  background: #f4f3ef;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.page-hero-inner {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-hero-title {
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.16em;
  font-weight: 500;
  color: #0c0b06;
  text-align: center;
}

.state-msg {
  padding: 40px 0;
  text-align: center;
  color: #0c0b06;
  font-size: 13px;
}

.state-err {
  color: #a94442;
}

.treatments-inner {
  padding: 34px 0 44px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.card {
  background: transparent;
  width: 100%;
}

.card,
.thumb-img,
.btn {
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease,
    color 180ms ease, filter 180ms ease;
}

@media (hover: hover) and (pointer: fine) {
  .card:hover {
    transform: translateY(-2px);
  }

  .card:hover .thumb-img {
    transform: scale(1.03);
  }

  .card:hover .btn {
    background: #b08a4a;
    border-color: #b08a4a;
    color: #fff;
  }
}

.thumb {
  background: #eee;
  overflow: hidden;
  width: 100%;
}

.thumb-img {
  width: 100%;
  max-width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;
  transform: scale(1.001);
}

.meta {
  padding: 16px 0 0;
  text-align: center;
}

.label {
  font-size: 14px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0c0b06;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn {
  margin-top: 12px;
  height: 30px;
  min-width: 110px;
  padding: 0 14px;
  border-radius: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  color: #0c0b06;
  font-size: 10px;
  letter-spacing: 0.12em;
  cursor: pointer;
}

.btn:hover {
  border-color: rgba(176, 138, 74, 0.55);
  color: #b08a4a;
}

.detail-wrap {
  padding: 28px 0 48px;
}

.detail-body {
  font-size: 14px;
  line-height: 1.75;
  color: #0c0b06;
}

.detail-body >>> img {
  max-width: 100%;
  height: auto;
}

.detail-body >>> p {
  margin: 0 0 1em;
}

.cta-band {
  padding: 44px 0;
  background: #f4f3ef;
}

.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.cta-title {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.cta-sub {
  margin: 10px 0 0;
  color: #0c0b06;
  font-size: 12px;
  line-height: 1.7;
}

.cta-btn {
  height: 36px;
  min-width: 150px;
  padding: 0 26px;
  border: none;
  border-radius: 0;
  background: #b08a4a;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.14em;
  cursor: pointer;
}

.cta-btn:hover {
  filter: brightness(0.96);
}

@media (max-width: 1000px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 0 12px;
    box-sizing: border-box;
  }

  .thumb-img {
    height: 240px;
  }
}

@media (max-width: 720px) {
  .card {
    width: 100%;
  }

  .container {
    padding: 0 12px;
  }

  .page-hero-inner {
    height: 96px;
  }

  .treatments-inner {
    padding: 26px 0 34px;
  }

  .grid {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  .thumb-img {
    height: 230px;
    transform: none;
  }

  .label {
    font-size: 14px;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .cta-btn {
    width: 100%;
    max-width: 220px;
  }
}
</style>
